import { z } from "zod";
import {
  ContentAlignment,
  FontSizeSetting,
  FormJourneyQuestionType,
  FormStatus,
  LogicActionType,
  LogicConditionAdditionalType,
  LogicConditionOperation,
  LogicConditionType,
  TransitionType,
  LogoPosition,
  JourneyItemMediaPosition,
  FormType,
  ConversationRole,
  FormElementType,
  Plan,
  GDPRSetting,
  FormSnippetType,
  FormJourneyQuestionStrictness,
  CalendarProvider,
} from "./enums";
import { EmbedTriggerIconType } from "./embedTrigger";
import { Media } from "./media";

// JsonValue type for selectOptions, additionalSettings, scoreValues, media, hiddenFields
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema) as z.ZodType<JsonValue[]>,
    z.record(z.string(), JsonValueSchema) as z.ZodType<{ [key: string]: JsonValue }>
  ])
) as z.ZodType<JsonValue>;

export const JourneySelectOption = z.object({
  id: z.string(),
  order: z.number(),
  value: z.string(),
  mediaSettings: z.object({
    media: Media.nullish(),
  }).nullish()
})
export type JourneySelectOption = z.infer<typeof JourneySelectOption>;

export const FormSnippet = z.object({
  type: FormSnippetType,
  id: z.string(),
  content: z.string()
    .max(1000, { message: 'Content cannot be more than 1000 character' }),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  formId: z.string(),
})
export type FormSnippet = z.infer<typeof FormSnippet>;

export const FormJourneyItemContactInfoAdditionalSettings = z.object({
  first_name: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  last_name: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  email: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  phone: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  company: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
})
export type FormJourneyItemContactInfoAdditionalSettings = z.infer<typeof FormJourneyItemContactInfoAdditionalSettings>

export const FormJourneyItemAddressAdditionalSettings = z.object({
  address_line_1: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  address_line_2: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  city: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  region: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  postal_code: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
  country: z.object({ required: z.boolean(), visible: z.boolean(), }).optional(),
})
export type FormJourneyItemAddressAdditionalSettings = z.infer<typeof FormJourneyItemAddressAdditionalSettings>

export const FormJourneyItemAdditionalSettings = z.intersection(
  FormJourneyItemContactInfoAdditionalSettings,
  FormJourneyItemAddressAdditionalSettings
)
export type FormJourneyItemAdditionalSettings = z.infer<typeof FormJourneyItemAdditionalSettings>

export const SchedulerSettings = z.object({
  provider: CalendarProvider,
  connectionId: z.string(),
  calendarId: z.string(),
  calendarName: z.string().nullish(),
  durationMinutes: z.number().int().positive().nullish(),
  eventTitle: z.string().nullish(),
  inviteeNameBlockId: z.string().nullish(),
  inviteeEmailBlockId: z.string().nullish(),
  /** Google Calendar inline embed — full share/embed URL or derived from calendarId. */
  bookingPageUrl: z.string().nullish(),
})
export type SchedulerSettings = z.infer<typeof SchedulerSettings>

export const FormJourneyItemLogicCondition = z.object({
  conditionOperation: LogicConditionOperation,
  additionalType: LogicConditionAdditionalType.nullable(),
  id: z.string(),
  value: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  sourceBlockId: z.string(),
  parentLogicItemId: z.string(),
})
export type FormJourneyItemLogicCondition = z.infer<typeof FormJourneyItemLogicCondition>

export const FormJourneyItemLogic = z.object({
  conditionType: LogicConditionType,
  actionType: LogicActionType,
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  targetBlockId: z.string().nullable(),
  endOnCompletion: z.boolean().nullable(),
  parentJourneyItemId: z.string(),
  conditions: z.array(FormJourneyItemLogicCondition),
})
export type FormJourneyItemLogic = z.infer<typeof FormJourneyItemLogic>

export const FormJourneyItem = z.object({
  type: FormJourneyQuestionType,
  strictness: FormJourneyQuestionStrictness,
  contentAlignment: ContentAlignment,
  mediaPosition: JourneyItemMediaPosition,
  mobileMediaPosition: JourneyItemMediaPosition,
  id: z.string(),
  blockId: z.string(),
  content: z.string()
    .min(1, { message: 'Content is required' }),
  manditory: z.boolean(),
  horizontalAlignment: z.boolean(),
  otherOption: z.boolean().nullish(),
  order: z.number(),
  selectOptions: z.array(JourneySelectOption).nullish(),
  multiSelect: z.boolean().nullable(),
  youtubeVideoURL: z.string().nullable(),
  internalName: z.string().nullable(),
  consentTitle: z.string().nullable(),
  minRange: z.number().nullable(),
  maxRange: z.number().nullable(),
  minLabel: z.string().nullable(),
  maxLabel: z.string().nullable(),
  secondaryContent: z.string().nullable(),
  thirdContent: z.string().nullable(),
  forthContent: z.string().nullable(),
  icon: z.string().nullable(),
  additionalSettings: FormJourneyItemAdditionalSettings.nullish(),
  schedulerSettings: SchedulerSettings.nullish(),
  targetBlockId: z.string().nullable(),
  endOnCompletion: z.boolean().nullable(),
  scoreValues: z.union([
    z.array(z.number().nullable()),
    z.record(z.string(), z.number().nullable())
  ]).nullish(),
  responseTableVisible: z.boolean().nullable(),
  opacity: z.number().nullable(),
  media: Media.nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  formId: z.string(),
  parentId: z.string().nullish(),
  logicItems: z.array(FormJourneyItemLogic).nullish(),
  sourceLogicItems: z.array(FormJourneyItemLogic).nullish(),
  targetLogicItems: z.array(FormJourneyItemLogic).nullish(),
})
export type FormJourneyItem = z.infer<typeof FormJourneyItem>;

// Shared design fields a theme owns — these live both inline on a Form and on a
// FormTheme. Logo is intentionally NOT here: it's per-form branding, not themed.
export const FormThemeDesign = z.object({
  backgroundColorSetting: z.string(),
  textColorSetting: z.string(),
  buttonColorSetting: z.string(),
  buttonTextColorSetting: z.string(),
  // Dedicated color for answer fields (choices, inputs, date picker).
  // Null = fall back to the button colors (pre-existing behavior).
  answerColorSetting: z.string().nullish(),
  fontFamilySetting: z.string(),
  fontSizeSetting: FontSizeSetting,
  elementType: FormElementType,
});
export type FormThemeDesign = z.infer<typeof FormThemeDesign>;

export const FormTheme = FormThemeDesign.extend({
  id: z.string(),
  workspaceId: z.string(),
  brandKitId: z.string().nullish(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Convenience: how many forms currently link to this theme (live-link notice).
  formCount: z.number().nullish(),
});
export type FormTheme = z.infer<typeof FormTheme>;

export const CreateFormThemeParams = FormThemeDesign.partial().extend({
  workspaceId: z.string(),
  name: z.string().min(1).max(255),
});
export type CreateFormThemeParams = z.infer<typeof CreateFormThemeParams>;

export const UpdateFormThemeParams = FormThemeDesign.partial().extend({
  id: z.string(),
  name: z.string().min(1).max(255).optional(),
});
export type UpdateFormThemeParams = z.infer<typeof UpdateFormThemeParams>;

export const DeleteFormThemeParams = z.object({ id: z.string() });
export type DeleteFormThemeParams = z.infer<typeof DeleteFormThemeParams>;

export const LeadForm = z.object({
  fontSizeSetting: FontSizeSetting,
  // Independent size for welcome/end screen titles; null = derive from
  // fontSizeSetting (the renderer's 170% default). Set by the Typeform importer.
  screenFontSizeSetting: FontSizeSetting.nullish(),
  transitionType: TransitionType,
  status: FormStatus,
  type: FormType,
  elementType: FormElementType,
  gdprSetting: GDPRSetting,
  gdprRequired: z.boolean(),
  id: z.string(),
  name: z.string(),
  overview: z.string().nullable(),
  about: z.string().nullable(),
  tone: z.string().nullable(),
  assistantId: z.string().nullable(),
  backgroundColorSetting: z.string(),
  textColorSetting: z.string(),
  buttonColorSetting: z.string(),
  buttonTextColorSetting: z.string(),
  answerColorSetting: z.string().nullish(),
  embedTriggerBgColor: z.string().nullish(),
  embedTriggerIconColor: z.string().nullish(),
  embedTriggerIconType: EmbedTriggerIconType.optional(),
  embedDrawerTriggerLabel: z.string().max(48).nullish(),
  fontFamilySetting: z.string().nullable(),
  hideBuiltWithBadge: z.boolean(),
  closed: z.boolean(),
  closedMessage: z.string().nullable(),
  legalDisclaimer: z.boolean(),
  legalMessage: z.string().nullable(),
  notifications: z.boolean(),
  notificationEmail: z.string().nullable(),
  logoUrl: z.string().nullish(),
  // Optional so pre-migration rows and literal fixtures stay valid; missing
  // means TOP_LEFT (the historical placement).
  logoPosition: LogoPosition.optional(),
  skipWelcomeScreen: z.boolean(),
  // Welcome screen "Takes X minutes" badge; optional so pre-migration rows and
  // literal fixtures stay valid. Treat missing as ON (`!== false`).
  showTimeToComplete: z.boolean().optional(),
  // A/B/C letter chips + keyboard letter shortcuts on answer buttons; optional
  // for the same rollout reasons. Treat missing as ON (`!== false`).
  showAnswerLetters: z.boolean().optional(),
  googleTagManager: z.string().nullable(),
  metaPixel: z.string().nullable(),
  version: z.number(),
  collectionId: z.string(),
  hiddenFields: z.array(z.string()).nullish(),
  zapierTargetUrl: z.string().nullable(),
  transitionSpeed: z.number(),
  customEndScreen: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  workspaceId: z.string(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  consentMessageTitle: z.string().nullable(),
  metaImageUrl: z.string().nullable(),
  journeyItems: z.array(FormJourneyItem).nullish(),
  snippets: FormSnippet.array().nullish(),
  numberOfResponses: z.number().nullish(),
  plan: Plan.nullish(),
  // When set, the *Setting fields above are resolved from this theme at fetch
  // time. The editor reads `themeId`/`theme` to route design edits to the theme.
  themeId: z.string().nullish(),
  theme: FormTheme.nullish(),
  /** When set, the form lives inside a user-created `FormFolder` for organization. */
  folderId: z.string().nullish(),
})
export type LeadForm = z.infer<typeof LeadForm>;

export const PublishedLeadForm = z.object({
  fontSizeSetting: FontSizeSetting,
  screenFontSizeSetting: FontSizeSetting.nullish(),
  transitionType: TransitionType,
  status: FormStatus,
  type: FormType,
  elementType: FormElementType,
  gdprSetting: GDPRSetting,
  gdprRequired: z.boolean(),
  id: z.string(),
  name: z.string(),
  overview: z.string().nullable(),
  about: z.string().nullable(),
  tone: z.string().nullable(),
  assistantId: z.string().nullable(),
  backgroundColorSetting: z.string(),
  textColorSetting: z.string(),
  buttonColorSetting: z.string(),
  buttonTextColorSetting: z.string(),
  answerColorSetting: z.string().nullish(),
  fontFamilySetting: z.string().nullable(),
  hideBuiltWithBadge: z.boolean(),
  closed: z.boolean(),
  closedMessage: z.string().nullable(),
  legalDisclaimer: z.boolean(),
  legalMessage: z.string().nullable(),
  notifications: z.boolean(),
  notificationEmail: z.string().nullable(),
  logoUrl: z.string().nullish(),
  // Optional so pre-migration rows and literal fixtures stay valid; missing
  // means TOP_LEFT (the historical placement).
  logoPosition: LogoPosition.optional(),
  skipWelcomeScreen: z.boolean(),
  // Welcome screen "Takes X minutes" badge; optional so pre-migration rows and
  // literal fixtures stay valid. Treat missing as ON (`!== false`).
  showTimeToComplete: z.boolean().optional(),
  // A/B/C letter chips + keyboard letter shortcuts on answer buttons; optional
  // for the same rollout reasons. Treat missing as ON (`!== false`).
  showAnswerLetters: z.boolean().optional(),
  googleTagManager: z.string().nullable(),
  metaPixel: z.string().nullable(),
  version: z.number(),
  collectionId: z.string(),
  hiddenFields: JsonValueSchema.nullable(),
  zapierTargetUrl: z.string().nullable(),
  transitionSpeed: z.number(),
  customEndScreen: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  workspaceId: z.string(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  consentMessageTitle: z.string().nullable(),
  metaImageUrl: z.string().nullable(),
  journeyItems: z.array(FormJourneyItem),
  snippets: FormSnippet.array(),
})
export type PublishedLeadForm = z.infer<typeof PublishedLeadForm>;

// Data
export const LeadFormEditorData = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  overview: z.string(),
  tone: z.string()
    .min(1, { message: 'Tone is required' })
    .min(4, { message: 'Tone description must be at least 4 characters long(polite, witty)' })
    .max(40, { message: 'Tone description must be less than 40 characters' }),
  customEndScreen: z.boolean(),
  backgroundColorSetting: z.string(),
  textColorSetting: z.string(),
  buttonColorSetting: z.string(),
  fontSizeSetting: FontSizeSetting,
  screenFontSizeSetting: FontSizeSetting.nullish(),
  transitionType: TransitionType,
  elementType: FormElementType,
  transitionSpeed: z.number(),
  buttonTextColorSetting: z.string(),
  answerColorSetting: z.string().nullish(),
  embedTriggerBgColor: z.string().nullish(),
  embedTriggerIconColor: z.string().nullish(),
  embedTriggerIconType: EmbedTriggerIconType.nullish(),
  embedDrawerTriggerLabel: z.string().max(48).nullish(),
  fontFamilySetting: z.string(),
  hideBuiltWithBadge: z.boolean(),
  closed: z.boolean(),
  closedMessage: z.string().nullish(),
  legalDisclaimer: z.boolean(),
  legalMessage: z.string().nullish(),
  notifications: z.boolean(),
  notificationEmail: z.string().nullish(),
  logoUrl: z.string().nullish(),
  // Optional so pre-migration rows and literal fixtures stay valid; missing
  // means TOP_LEFT (the historical placement).
  logoPosition: LogoPosition.optional(),
  skipWelcomeScreen: z.boolean(),
  // Welcome screen "Takes X minutes" badge; optional so pre-migration rows and
  // literal fixtures stay valid. Treat missing as ON (`!== false`).
  showTimeToComplete: z.boolean().optional(),
  // A/B/C letter chips + keyboard letter shortcuts on answer buttons; optional
  // for the same rollout reasons. Treat missing as ON (`!== false`).
  showAnswerLetters: z.boolean().optional(),
  googleTagManager: z.string().nullish(),
  metaPixel: z.string().nullish(),
  hiddenFields: z.array(z.string()),
  about: z.string(),
  journeyItems: z.array(FormJourneyItem).min(1, { message: 'Must have at least 1 journey item' }),
  snippets: z.array(FormSnippet).optional(),
  // Metadata fields for share page
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaImageUrl: z.string().optional(),
  gdprSetting: GDPRSetting,
  gdprRequired: z.boolean(),
  consentMessageTitle: z.string().nullish(),
  themeId: z.string().nullish(),
});
export type LeadFormEditorData = z.infer<typeof LeadFormEditorData>;

export const SaveLeadFormDraftParams = z.object({
  collectionId: z.string(),
  name: z.string().optional(),
  overview: z.string().optional(),
  about: z.string().optional(),
  tone: z.string().optional(),
  backgroundColorSetting: z.string(),
  textColorSetting: z.string(),
  buttonColorSetting: z.string(),
  buttonTextColorSetting: z.string(),
  answerColorSetting: z.string().nullish(),
  embedTriggerBgColor: z.string().nullish(),
  embedTriggerIconColor: z.string().nullish(),
  embedTriggerIconType: EmbedTriggerIconType.nullish(),
  embedDrawerTriggerLabel: z.string().max(48).nullish(),
  fontFamilySetting: z.string(),
  hideBuiltWithBadge: z.boolean(),
  closed: z.boolean(),
  closedMessage: z.string().nullish(),
  legalDisclaimer: z.boolean(),
  legalMessage: z.string().nullish(),
  notifications: z.boolean(),
  notificationEmail: z.string().nullish(),
  logoUrl: z.string().nullish(),
  // Optional so pre-migration rows and literal fixtures stay valid; missing
  // means TOP_LEFT (the historical placement).
  logoPosition: LogoPosition.optional(),
  transitionType: TransitionType,
  transitionSpeed: z.number(),
  elementType: FormElementType,
  skipWelcomeScreen: z.boolean(),
  // Welcome screen "Takes X minutes" badge; optional so pre-migration rows and
  // literal fixtures stay valid. Treat missing as ON (`!== false`).
  showTimeToComplete: z.boolean().optional(),
  // A/B/C letter chips + keyboard letter shortcuts on answer buttons; optional
  // for the same rollout reasons. Treat missing as ON (`!== false`).
  showAnswerLetters: z.boolean().optional(),
  googleTagManager: z.string().nullish(),
  metaPixel: z.string().nullish(),
  assistantId: z.string().nullish(),
  fontSizeSetting: FontSizeSetting,
  screenFontSizeSetting: FontSizeSetting.nullish(),
  customEndScreen: z.boolean().optional(),
  hiddenFields: z.array(z.string()),
  journeyItems: z.array(FormJourneyItem).optional(),
  snippets: z.array(FormSnippet).optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaImageUrl: z.string().optional().nullable(),
  gdprSetting: GDPRSetting,
  gdprRequired: z.boolean(),
  consentMessageTitle: z.string().nullish(),
  themeId: z.string().nullish(),
})

export type SaveLeadFormDraftParams = z.infer<typeof SaveLeadFormDraftParams>

export const SaveLeadFormMetadataParams = z.object({
  collectionId: z.string(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaImageUrl: z.string().optional().nullable(),
  hiddenFields: z.array(z.string()).optional(),
});

export type SaveLeadFormMetadataParams = z.infer<typeof SaveLeadFormMetadataParams>

export const SaveLeadFormHiddenFieldsParams = z.object({
  collectionId: z.string(),
  hiddenFields: z.array(z.string()),
});

export type SaveLeadFormHiddenFieldsParams = z.infer<typeof SaveLeadFormHiddenFieldsParams>

export const LeadFormTemplateType = z.enum(['product-recommendation', 'customer-feedback', 'lead-generation', 'event-registration', 'job-application'])
export type LeadFormTemplateType = z.infer<typeof LeadFormTemplateType>

export const RatingIcon = z.enum(['heart', 'star'])
export type RatingIcon = z.infer<typeof RatingIcon>

export const DateFormatType = z.enum(['MMDDYYYY', 'DDMMYYYY', 'YYYYMMDD'])
export type DateFormatType = z.infer<typeof DateFormatType>

export const FormJourneyItemDateAdditionalSettings = z.object({
  month: z.object({
    required: z.boolean(),
    visible: z.boolean(),
    value: z.number()
      .min(1, { message: 'Month must be between 1 and 12' })
      .max(12, { message: 'Month must be between 1 and 12' })
  }).optional(),
  day: z.object({
    required: z.boolean(),
    visible: z.boolean(),
    value: z.number()
      .min(1, { message: 'Day must be between 1 and 31' })
      .max(31, { message: 'Day must be between 1 and 31' })
  }).optional(),
  year: z.object({
    required: z.boolean(),
    visible: z.boolean(),
    value: z.number()
      .min(1900, { message: 'Year must be 1900 or later' })
      .max(new Date().getFullYear(), { message: `Year must be ${new Date().getFullYear()} or earlier` })
  }).optional(),
})
export type FormJourneyItemDateAdditionalSettings = z.infer<typeof FormJourneyItemDateAdditionalSettings>

export const Address = z.object({
  address_line_1: z.string().nullish(),
  address_line_2: z.string().nullish(),
  city: z.string().nullish(),
  region: z.string().nullish(),
  postal_code: z.string().nullish(),
  country: z.string().nullish(),
})
export type Address = z.infer<typeof Address>

export const ContactDetails = z.object({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  company: z.string().nullish(),
})
export type ContactDetails = z.infer<typeof ContactDetails>

export const ResponseMessages = z.object({
  id: z.string(),
  blockId: z.string(),
  response: z.string(),
  type: ConversationRole,
})
export type ResponseMessages = z.infer<typeof ResponseMessages>